/**
 * The guide player's behaviour (2026-10-14). The server writes every step into
 * the page; this turns the list into a player: install methods as tabs, one
 * step at a time, Back / Next, arrow keys, the list of all steps, and the
 * address (?method=scan&step=8) so support can send a reader to a step.
 */
/**
 * It runs twice over a page's life at most: inline, right after the server's
 * markup (so the reader's method is in place before the first paint), and from
 * the client route after an in-app navigation, where inline scripts do not
 * run. Self-contained on purpose: the inline copy is this function's source
 * text, so it may not reach for anything outside it.
 */
// eslint-disable-next-line max-lines-per-function, complexity -- one self-contained function: its source text is the inline script
export function guidePlayer(root) {
    const r = root;
    if (!r || r.gpLive)
        return;
    r.gpLive = true;
    const one = (sel) => r.querySelector(sel);
    const all = (sel) => Array.from(r.querySelectorAll(sel));
    const tabs = all('.gp-tab');
    const methods = tabs.map((t) => t.dataset['method'] ?? '');
    const panel = one('.gp-panel');
    const say = one('.gp-say');
    const tip = one('.gp-tip');
    const phone = one('.gp-phone');
    const prev = one('.gp-prev');
    const next = one('.gp-next');
    if (!panel || !say || !tip || !phone || !prev || !next || !methods.length)
        return;
    const media = (q) => !!window.matchMedia && window.matchMedia(q).matches;
    const reduce = media('(prefers-reduced-motion: reduce)');
    const pad = (n) => String(n).padStart(2, '0');
    const setText = (sel, text) => { const el = one(sel); if (el)
        el.textContent = text; };
    // Not replaceChildren(): Safari before 14 has no such method.
    const put = (box, node) => { box.textContent = ''; box.appendChild(node); };
    /** A method's steps in order: its own, then the ones every method shares. */
    const seq = (m) => all(`.gp-block[data-method="${m}"] .gp-step, .gp-block[data-shared] .gp-step`);
    const q = new URLSearchParams(window.location.search);
    const asked = q.get('method') ?? '';
    const preferred = (media('(pointer: coarse)') ? r.dataset['phone'] : r.dataset['desk']) ?? '';
    let method = [asked, preferred].find((m) => methods.includes(m)) ?? methods[0];
    let i = Math.max(0, (parseInt(q.get('step') ?? '1', 10) || 1) - 1);
    const paintMethod = () => {
        r.dataset['method'] = method;
        tabs.forEach((t) => {
            const on = t.dataset['method'] === method;
            t.setAttribute('aria-selected', String(on));
            t.tabIndex = on ? 0 : -1;
            if (on)
                panel.setAttribute('aria-labelledby', t.id);
        });
        all('.gp-hint, .gp-board__list').forEach((el) => { el.hidden = el.dataset['method'] !== method; });
    };
    /** The step's words, its tip and its screen, copied from the list onto the
     *  stage. The tip has a slot of its own: on a phone it goes under the screen. */
    const paintStep = (step, dir) => {
        const text = step.querySelector('.gp-step__text')?.cloneNode(true);
        const hint = text?.querySelector('.gp-step__tip');
        tip.textContent = '';
        if (hint)
            tip.appendChild(hint);
        if (text)
            put(say, text);
        const fig = step.querySelector('.manual-screen');
        if (!fig)
            return;
        const copy = fig.cloneNode(true);
        if (dir && !reduce)
            copy.classList.add(dir > 0 ? 'is-fwd' : 'is-back');
        put(phone, copy);
    };
    /** Part, count, progress and the buttons' ends. */
    const paintMeta = (step, steps) => {
        const part = step.dataset['part'] ?? 'get';
        const key = part[0].toUpperCase() + part.slice(1);
        setText('.gp-part', r.dataset['part' + key] ?? '');
        setText('.gp-sub', r.dataset['sub' + key] ?? '');
        setText('.gp-count__n', pad(i + 1));
        setText('.gp-count__t', pad(steps.length));
        all('.gp-seg').forEach((s) => {
            const inPart = steps.filter((x) => x.dataset['part'] === s.dataset['part']);
            const done = inPart.filter((x) => steps.indexOf(x) <= i).length;
            s.style.setProperty('--gp-fill', String(inPart.length ? done / inPart.length : 0));
        });
        prev.setAttribute('aria-disabled', String(i === 0));
        next.setAttribute('aria-disabled', String(i === steps.length - 1));
    };
    /** The list of all steps: done, now, still to come. */
    const paintBoard = () => {
        const now = r.dataset['lNow'] ?? '';
        all(`.gp-board__list[data-method="${method}"] .gp-row`).forEach((row, j) => {
            row.classList.toggle('is-now', j === i);
            row.classList.toggle('is-done', j < i);
            if (j === i)
                row.setAttribute('aria-current', 'step');
            else
                row.removeAttribute('aria-current');
            const s = row.querySelector('.gp-row__s');
            if (s)
                s.textContent = j === i ? now : '';
        });
    };
    const writeAddress = () => {
        if (!window.history?.replaceState)
            return;
        const p = new URLSearchParams(window.location.search);
        p.set('method', method);
        p.set('step', String(i + 1));
        window.history.replaceState(window.history.state, '', `${window.location.pathname}?${p.toString()}${window.location.hash}`);
    };
    const show = (dir, write) => {
        const steps = seq(method);
        i = Math.min(Math.max(i, 0), steps.length - 1);
        const step = steps[i];
        if (!step)
            return;
        paintMethod();
        paintStep(step, dir);
        paintMeta(step, steps);
        paintBoard();
        if (write)
            writeAddress();
    };
    /** Keep the step in view. On a phone, where Back / Next are pinned to the
     *  bottom of the window, also keep what the screen asks to tap above them,
     *  scrolling the part label away if it must but never the words. */
    const reveal = () => {
        const meta = one('.gp-meta');
        const nav = one('.gp-nav');
        if (!meta || !nav || typeof window.scrollBy !== 'function')
            return;
        const top = meta.getBoundingClientRect().top;
        let delta = top < 56 || top > window.innerHeight * 0.6 ? top - 72 : 0;
        const ring = phone.querySelector('.gs-tap');
        if (ring && getComputedStyle(nav).position === 'sticky') {
            const target = (ring.closest('.gs-row, .gs-btn, .gs-link, .gs-tap-host, .gs-setup__top') ?? ring).getBoundingClientRect().bottom - delta;
            const cover = window.innerHeight - nav.getBoundingClientRect().height * 0.78;
            const words = say.getBoundingClientRect().top - delta;
            delta += Math.min(Math.max(0, target - cover + 8), Math.max(0, words - 56));
        }
        if (delta)
            window.scrollBy({ top: delta, behavior: reduce ? 'auto' : 'smooth' });
    };
    const go = (to) => {
        if (to < 0 || to >= seq(method).length || to === i)
            return;
        const dir = to > i ? 1 : -1;
        i = to;
        show(dir, true);
        reveal();
    };
    const pick = (m, focus) => {
        if (!methods.includes(m))
            return;
        if (m !== method) {
            method = m;
            i = 0;
            show(0, true);
        }
        if (focus)
            tabs[methods.indexOf(m)]?.focus();
    };
    r.addEventListener('click', (e) => {
        const el = e.target;
        const tab = el?.closest('.gp-tab');
        const row = el?.closest('.gp-row');
        if (tab)
            pick(tab.dataset['method'] ?? '', false);
        else if (el?.closest('.gp-prev'))
            go(i - 1);
        else if (el?.closest('.gp-next'))
            go(i + 1);
        else if (row)
            go(parseInt(row.dataset['i'] ?? '0', 10));
    });
    // Arrows: between methods on the tab list (the ARIA tabs pattern); between
    // steps anywhere else while the player is on screen, unless the reader is
    // typing or in another widget. Listened on the document because a mouse
    // click leaves the focus on <body> in Safari and Firefox; it lets go once
    // the page has moved on and the player is gone.
    const tabKey = {
        ArrowRight: (k) => k + 1, ArrowLeft: (k) => k - 1, Home: () => 0, End: () => methods.length - 1,
    };
    const stepKey = { ArrowRight: 1, ArrowLeft: -1 };
    const busy = (el) => !!el.closest('input, textarea, select, [contenteditable], [role="tablist"], [role="slider"], [role="menu"]');
    const inView = () => {
        const b = r.getBoundingClientRect();
        return b.top < window.innerHeight && b.bottom > 0;
    };
    const modified = (e) => e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey;
    const onKey = (e) => {
        if (!r.isConnected) {
            document.removeEventListener('keydown', onKey);
            return;
        }
        if (modified(e))
            return;
        const el = e.target instanceof Element ? e.target : document.body;
        const toTab = el.closest('.gp-tab') ? tabKey[e.key] : undefined;
        const toStep = !busy(el) && inView() ? stepKey[e.key] : undefined;
        if (toTab) {
            e.preventDefault();
            const k = (toTab(methods.indexOf(method)) + methods.length) % methods.length;
            pick(methods[k] ?? method, true);
        }
        else if (toStep) {
            e.preventDefault();
            go(i + toStep);
        }
    };
    document.addEventListener('keydown', onKey);
    // A link to a step (support sends them) opens on that step; the client
    // route scrolls it into view once the router is done with the page.
    show(0, false);
    // Read by the page's fallback: a player that never got here gives the
    // reader the plain list back.
    r.gpReady = true;
}
